import { useCallback, useEffect, useRef, useState } from "react";
import {
  INPUT_LENGTH_IX,
  INPUT_SIGNAL_IX,
  SIGINT,
  type IdeOutputTab,
  type LessonTestBlockWorkerResult,
  type MainToWorkerMessage,
  type ProjectWorkerFile,
  type WorkerLessonTestInput,
  type WorkerToMainMessage,
} from "./protocol";

export type IseRuntimePhase = "loading" | "ready" | "running" | "awaiting_input";

const INPUT_CTRL_BYTES = 8;
const INPUT_TEXT_BYTES = 65536;
const INTERRUPT_BYTES = 1;

export type ConsoleLine = { stream: "stdout" | "stderr" | "info"; text: string };

export type PythonRuntimeOptions = {
  /** Receives the project file tree captured from the Pyodide FS after each `runProject` ends (finished, error, or stopped). */
  onProjectFilesSync?: (files: ProjectWorkerFile[], skippedPaths: string[]) => void;
  /** When set, deferred turtle frames wait for this before appearing (e.g. until project save settles). */
  deferTurtleDisplay?: () => Promise<void>;
};

function createSharedRuntimeBuffers() {
  const inputCtrl = new SharedArrayBuffer(INPUT_CTRL_BYTES);
  const inputText = new SharedArrayBuffer(INPUT_TEXT_BYTES);
  const interruptFlag = new SharedArrayBuffer(INTERRUPT_BYTES);
  return { inputCtrl, inputText, interruptFlag };
}

export function usePythonRuntime(options?: PythonRuntimeOptions) {
  const onProjectFilesSyncRef = useRef<PythonRuntimeOptions["onProjectFilesSync"]>(undefined);
  onProjectFilesSyncRef.current = options?.onProjectFilesSync;
  const deferTurtleDisplayRef = useRef<PythonRuntimeOptions["deferTurtleDisplay"]>(undefined);
  deferTurtleDisplayRef.current = options?.deferTurtleDisplay;
  const pendingTurtleHtmlRef = useRef<string | null>(null);
  const workerRef = useRef<Worker | null>(null);
  const inputTextViewRef = useRef<Uint8Array | null>(null);
  const inputCtrlViewRef = useRef<Int32Array | null>(null);
  const interruptViewRef = useRef<Uint8Array | null>(null);
  const runStartedAtRef = useRef<number | null>(null);
  const testRunPromiseRef = useRef<{
    resolve: (v: LessonTestBlockWorkerResult[]) => void;
    reject: (e: Error) => void;
  } | null>(null);

  const [phase, setPhase] = useState<IseRuntimePhase>("loading");
  const [runtimeError, setRuntimeError] = useState<string | null>(null);
  const [lines, setLines] = useState<ConsoleLine[]>([]);
  const [inputPrompt, setInputPrompt] = useState<string>("");
  const [canUseSharedMemory, setCanUseSharedMemory] = useState(true);
  const [turtleHtml, setTurtleHtml] = useState<string | null>(null);
  const [turtleTabVisible, setTurtleTabVisible] = useState(false);
  const [outputTab, setOutputTab] = useState<IdeOutputTab>("console");

  const appendLine = useCallback((line: ConsoleLine) => {
    setLines((prev) => {
      const next = [...prev, line];
      return next.length > 2000 ? next.slice(-2000) : next;
    });
  }, []);

  const appendRunSummaryLine = useCallback(
    (verb: "finished" | "stopped") => {
      const startedAt = runStartedAtRef.current;
      runStartedAtRef.current = null;
      if (startedAt === null) return;
      const elapsedMs = Math.max(1, Math.round(performance.now() - startedAt));
      appendLine({ stream: "info", text: `- Run ${verb} in ${elapsedMs} ms -` });
    },
    [appendLine]
  );

  const appendRunFinishedLine = useCallback(() => {
    appendRunSummaryLine("finished");
  }, [appendRunSummaryLine]);

  const appendRunStoppedLine = useCallback(() => {
    appendRunSummaryLine("stopped");
  }, [appendRunSummaryLine]);

  const commitTurtleDisplay = useCallback((html: string) => {
    setTurtleHtml(html);
    setTurtleTabVisible(true);
    setOutputTab("turtle");
  }, []);

  const flushPendingTurtleDisplay = useCallback(async () => {
    const html = pendingTurtleHtmlRef.current;
    if (!html) return;
    pendingTurtleHtmlRef.current = null;
    const defer = deferTurtleDisplayRef.current;
    if (defer) {
      try {
        await defer();
      } catch {
        /* still show the drawing */
      }
    }
    commitTurtleDisplay(html);
  }, [commitTurtleDisplay]);

  useEffect(() => {
    const isolated =
      typeof globalThis.crossOriginIsolated === "boolean" ? globalThis.crossOriginIsolated : false;
    const sabGlobal = typeof globalThis.SharedArrayBuffer !== "undefined";
    const secure =
      typeof globalThis.isSecureContext === "boolean" ? globalThis.isSecureContext : false;
    const host =
      typeof globalThis.location !== "undefined" ? globalThis.location.hostname : "(unknown)";

    /**
     * Browsers only expose SharedArrayBuffer in a [secure context] that is also cross-origin isolated
     * (COOP + COEP). Opening the dev server as http://192.168.x.x (vite --host) is usually NOT a
     * secure context, so Chrome hides SharedArrayBuffer even though Vite sends the headers.
     */
    if (!secure || !isolated || !sabGlobal) {
      setCanUseSharedMemory(false);
      setPhase("ready");
      const diag = `(secureContext=${String(secure)}, crossOriginIsolated=${String(isolated)}, SharedArrayBuffer=${String(sabGlobal)}, host=${host})`;
      if (!secure) {
        setRuntimeError(
          `This origin is not a secure context, so SharedArrayBuffer is disabled. ${diag} Use https:// or open via localhost / 127.0.0.1 only. For LAN testing, run "npm run dev:https-host" from apps/web, trust the dev certificate in the browser, and use the https URL Vite prints (or deploy production with HTTPS).`,
        );
        return;
      }
      if (!isolated) {
        setRuntimeError(
          `This page is not cross-origin isolated, so the IDE cannot use shared memory for Stop and input(). ${diag} The HTML response must include Cross-Origin-Opener-Policy: same-origin and Cross-Origin-Embedder-Policy: require-corp. Vite sets this in dev; production must attach the same headers on the document (e.g. CloudFront response headers policy on the apps/web distribution, see infra/cdk/lib/web-stack.ts). If you use a proxy (Cloudflare, etc.), ensure it forwards those headers. Avoid iframes that break isolation.`,
        );
        return;
      }
      setRuntimeError(
        `SharedArrayBuffer is missing even though the page looks isolated. ${diag} Try updating the browser; report this if it persists.`,
      );
      return;
    }

    const { inputCtrl, inputText, interruptFlag } = createSharedRuntimeBuffers();
    inputTextViewRef.current = new Uint8Array(inputText);
    inputCtrlViewRef.current = new Int32Array(inputCtrl);
    interruptViewRef.current = new Uint8Array(interruptFlag);

    const worker = new Worker(new URL("./pyodide-worker.ts", import.meta.url), { type: "module" });
    workerRef.current = worker;

    worker.onmessage = (e: MessageEvent<WorkerToMainMessage>) => {
      const msg = e.data;
      switch (msg.type) {
        case "ready":
          setPhase("ready");
          setRuntimeError(null);
          appendLine({ stream: "info", text: "Python runtime ready." });
          break;
        case "stdout":
          appendLine({ stream: "stdout", text: msg.text });
          break;
        case "stderr":
          appendLine({ stream: "stderr", text: msg.text });
          break;
        case "input_request":
          setInputPrompt(msg.prompt);
          setPhase("awaiting_input");
          break;
        case "turtle_clear":
          setTurtleHtml(null);
          setTurtleTabVisible(false);
          setOutputTab("console");
          break;
        case "turtle_display": {
          if (msg.html) commitTurtleDisplay(msg.html);
          break;
        }
        case "finished":
          if (msg.turtleHtml) pendingTurtleHtmlRef.current = msg.turtleHtml;
          setPhase("ready");
          appendRunFinishedLine();
          void flushPendingTurtleDisplay();
          break;
        case "stopped":
          setPhase("ready");
          appendRunStoppedLine();
          break;
        case "error":
          appendLine({ stream: "stderr", text: msg.message });
          setPhase("ready");
          appendRunFinishedLine();
          break;
        case "project_files_sync":
          onProjectFilesSyncRef.current?.(msg.files, msg.skippedPaths);
          break;
        case "test_results": {
          const pending = testRunPromiseRef.current;
          testRunPromiseRef.current = null;
          appendLine({ stream: "info", text: "- Lesson checks finished -" });
          pending?.resolve(msg.results);
          break;
        }
        case "tests_error": {
          const pending = testRunPromiseRef.current;
          testRunPromiseRef.current = null;
          pending?.reject(new Error(msg.message));
          break;
        }
        default:
          break;
      }
    };

    const init: MainToWorkerMessage = {
      type: "init",
      inputCtrl,
      inputText,
      interruptFlag,
    };
    // SharedArrayBuffer is NOT listed in postMessage's transfer list (it is not a "transferable"
    // like ArrayBuffer). It is shared via structured clone; listing it throws DataCloneError.
    worker.postMessage(init);

    return () => {
      const pending = testRunPromiseRef.current;
      testRunPromiseRef.current = null;
      pending?.reject(new Error("Python worker stopped."));
      worker.terminate();
      workerRef.current = null;
    };
  }, [appendLine, appendRunFinishedLine, appendRunStoppedLine, commitTurtleDisplay, flushPendingTurtleDisplay]);

  const run = useCallback(
    (code: string, filename = "main.py") => {
      const w = workerRef.current;
      if (!w || !canUseSharedMemory) return;
      runStartedAtRef.current = performance.now();
      setPhase("running");
      w.postMessage({ type: "run", code, filename } satisfies MainToWorkerMessage);
    },
    [canUseSharedMemory],
  );

  const runProject = useCallback(
    (files: readonly ProjectWorkerFile[], entryPath = "main.py") => {
      const w = workerRef.current;
      if (!w || !canUseSharedMemory) return;
      runStartedAtRef.current = performance.now();
      setPhase("running");
      w.postMessage({ type: "run_project", files: [...files], entryPath } satisfies MainToWorkerMessage);
    },
    [canUseSharedMemory],
  );

  const stop = useCallback(() => {
    const intBuf = interruptViewRef.current;
    if (intBuf) intBuf[0] = SIGINT;
    const w = workerRef.current;
    if (w) w.postMessage({ type: "stop" } satisfies MainToWorkerMessage);
  }, []);

  const submitInput = useCallback((line: string) => {
    const ctrl = inputCtrlViewRef.current;
    const textBuf = inputTextViewRef.current;
    if (!ctrl || !textBuf) return;

    const enc = new TextEncoder();
    const bytes = enc.encode(line + "\n");
    if (bytes.byteLength > textBuf.byteLength) {
      appendLine({ stream: "stderr", text: "Input too long for console buffer." });
      return;
    }
    textBuf.set(bytes);
    Atomics.store(ctrl, INPUT_LENGTH_IX, bytes.byteLength);
    Atomics.store(ctrl, INPUT_SIGNAL_IX, 1);
    Atomics.notify(ctrl, INPUT_SIGNAL_IX);
    setInputPrompt("");
    setPhase("running");
  }, [appendLine]);

  const clearConsole = useCallback(() => {
    setLines([]);
    pendingTurtleHtmlRef.current = null;
    setTurtleHtml(null);
    setTurtleTabVisible(false);
    setOutputTab("console");
  }, []);

  const runLessonTests = useCallback(
    (mainPy: string, tests: readonly WorkerLessonTestInput[]) => {
      const w = workerRef.current;
      if (!w || !canUseSharedMemory) {
        return Promise.reject(new Error("Python runtime is not ready for lesson checks."));
      }
      if (testRunPromiseRef.current) {
        return Promise.reject(new Error("Lesson checks are already running."));
      }
      return new Promise<LessonTestBlockWorkerResult[]>((resolve, reject) => {
        testRunPromiseRef.current = { resolve, reject };
        w.postMessage({ type: "run_tests", mainPy, tests: [...tests] } satisfies MainToWorkerMessage);
      });
    },
    [canUseSharedMemory]
  );

  return {
    phase: canUseSharedMemory ? phase : "ready",
    runtimeError,
    lines,
    inputPrompt,
    canUseSharedMemory,
    turtleHtml,
    turtleTabVisible,
    outputTab,
    setOutputTab,
    run,
    runProject,
    stop,
    submitInput,
    clearConsole,
    runLessonTests,
  };
}
